import { c as _c } from "react/compiler-runtime";
import { type PostFeedInput } from '@shared/all/validators';
import { type LocalPreferenceAction, localPreferenceActions, type LocalPreferenceState } from '../context/context';
import { type LocalPreferenceStore, useLocalPreferenceSelector, useLocalPreferenceState, useLocalPreferenceStore } from '../context/LocalPreferenceContext';
import calculateStartEnd from '../helpers/calculateStartEnd';
export { useLocalPreferenceSelector } from '../context/LocalPreferenceContext';

// The provider writes every change to storage (context/
// LocalPreferenceContext.tsx); these only change the store.
const createLocalPreferenceActions = (store: LocalPreferenceStore) => {
  const dispatch = (action: LocalPreferenceAction) => store.dispatch(action);
  return {
    updatedLocalPreference: (updatedSettings: Partial<LocalPreferenceState>) => {
      dispatch({
        type: localPreferenceActions.updatedLocalPreference,
        payload: {
          updatedSettings
        }
      });
    },
    updateMuteVideo: (isMuted: boolean) => {
      dispatch({
        type: localPreferenceActions.setMuteVideo,
        payload: isMuted
      });
    },
    setTheme: (theme: 'dark' | 'light' | 'system') => {
      dispatch({
        type: localPreferenceActions.SET_THEME,
        payload: theme
      });
    },
    /**
     * The feed query input for the current preferences, read when called (in
     * a query function), so calling it subscribes to nothing.
     */
    getPostParams: (params: PostFeedInput, cursor: string | number | undefined, strictFactiiiIds?: number[]) => {
      const state = store.getState();
      // For data mode, cursor is a createdAt date string used to calculate date ranges
      // For normal mode, cursor is a UUID string for Prisma cursor pagination
      const isDataMode = state.displayMode === 'data' && state.postSortBy;
      const dateRange = isDataMode && cursor ? calculateStartEnd(new Date(cursor), state.postSortBy!) : {};
      return {
        ...params,
        // Don't pass cursor for data mode - it uses start/end instead
        cursor: isDataMode ? undefined : cursor,
        type: state.postFilterType ?? undefined,
        filterBots: state.filterBot,
        filterNewAccounts: state.filterNew,
        showBoards: state.showBoards,
        factiiiIds: [...state.selectedFactiiis, ...(params.factiiiIds || [])],
        strictFactiiiIds: strictFactiiiIds ?? [],
        dataFilter: state.dataFilter || undefined,
        ...dateRange
      };
    }
  };
};

/**
 * Changes the preferences and builds feed params. Subscribes to nothing, so
 * the caller never rerenders from a preference change.
 */
export const useLocalPreferenceActions = () => {
  const $ = _c(2);
  const t0 = useLocalPreferenceStore();
  let t1;
  if ($[0] !== t0) {
    t1 = createLocalPreferenceActions(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/** The theme setting alone: what most styled components need. */
export const useThemeSetting = () => {
  return useLocalPreferenceSelector(_temp);
};

/**
 * The whole preferences state plus the actions. Rerenders on every change,
 * keystrokes in the data filter included: prefer useLocalPreferenceSelector,
 * useThemeSetting or useLocalPreferenceActions.
 */
const useLocalPreference = () => {
  const $ = _c(5);
  const {
    state
  } = useLocalPreferenceState();
  const t0 = useLocalPreferenceStore();
  let t1;
  if ($[0] !== t0) {
    t1 = createLocalPreferenceActions(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const actions = t1;
  let t2;
  if ($[2] !== actions || $[3] !== state) {
    t2 = {
      localPreferenceState: state,
      ...actions
    };
    $[2] = actions;
    $[3] = state;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export default useLocalPreference;
function _temp(state) {
  return state.theme;
}